/* eslint-disable owa-custom-rules/no-side-effects -- web components */
import {
    attr,
    css,
    type ElementStyles,
    FASTElement,
    html,
    type ViewTemplate,
    volatile,
    nullableNumberConverter,
    repeat,
    ref,
    observable,
} from '@microsoft/fast-element';
import classnames from 'owa-classnames';
import { isReadingPanePositionRight } from 'owa-mail-layout/lib/selectors/readingPanePosition';
import { mobxObservableProperty, mobxObserver } from '../utils/mobxObserver';
import { isGlobalFeatureEnabled } from 'owa-feature-flags';
import { getDensityModeString } from 'owa-fabric-theme';
import type { HoverActionKey } from 'owa-outlook-service-option-store';
import {
    getHoverSurfaceAction,
    getStore as getHoverSurfaceActionStore,
} from 'owa-surface-actions-option';
import { doesFolderOrViewSupportHoverAction } from 'owa-mail-list-item-shared';
import PropertyIcons from 'owa-mail-list-actions/lib/utils/conversationProperty/PropertyIcons';
import type ImportanceType from 'owa-service/lib/contract/ImportanceType';
import { type SmimeType } from 'smime-types';
import '../hoverIcons/HoverIcon';
import '../propertyIcons/PropertyIcon';
import { iconBarRegistry } from '../customElementRegistry';
import type { FlagMenu } from './FlagMenu';
import './FlagMenu';

const MAX_PROPERTY_ICONS = 4;

const template: ViewTemplate<IconBar> = html<IconBar>`<template>
    ${repeat(
        x => (x.neverShowHoverIcons ? [] : x.hoverActionKeys),
        html<HoverActionKey, IconBar>`
            <hover-icon
                class="${(x, c) => c.parent.getHoverIconClassNames(x)}"
                actionKey="${x => x ?? 'None'}"
                isPinned="${(_, c) => c.parent.isPinned}"
                hasUnread="${(_, c) => !!c.parent.unreadCount}"
                isFlagged="${(_, c) => c.parent.isFlagged}"
                isComplete="${(_, c) => c.parent.isComplete}"
                rowKey="${(_, c) => c.parent.rowKey}"
                tableViewId="${(_, c) => c.parent.tableViewId}"
            ></hover-icon>
        `
    )}
    ${repeat(
        x => (x.neverShowPropertyIcons ? [] : x.propertyIconKeys),
        html<PropertyIcons, IconBar>`
            <property-icon
                class="${(x, c) => c.parent.getPropertyIconClassNames(x)}"
                icon="${x => x ?? PropertyIcons.None}"
            ></property-icon>
        `
    )}
    <flag-menu
        ${ref('flagMenu')}
        isFlagged="${x => x.isFlagged}"
        isComplete="${x => x.isComplete}"
        rowKey="${x => x.rowKey}"
        tableViewId="${x => x.tableViewId}"
    ></flag-menu>
</template>`;

const styles: ElementStyles = css`
    :host {
        display: flex;
        align-items: center;
        height: 100%;
        top: 0px;
        right: var(--iconContainerRight, 0px);
        align-self: flex-end;
        flex: 0 0 auto;
        padding: 0;
        padding-bottom: var(--iconBarPaddingBottom, 0);
        margin-left: 8px;
    }

    @media screen and (forced-colors: active) {
        :host {
            background-color: HighlightText;
        }
    }

    .iconBarNoLeftMargin {
        margin-left: 0px;
    }
`;

@mobxObserver
class IconBar extends FASTElement {
    //#region Attributes
    @attr({ mode: 'boolean' })
    isSingleLine!: boolean;

    @attr({ mode: 'boolean' })
    neverShowHoverIcons!: boolean;

    @attr({ mode: 'boolean' })
    neverShowPropertyIcons!: boolean;

    @attr({ mode: 'boolean' })
    isInNotesFolder!: boolean;

    @attr({ mode: 'boolean' })
    isInOutboxFolder!: boolean;

    @attr({ mode: 'boolean' })
    canDelete!: boolean;

    @attr({ mode: 'boolean' })
    canArchive!: boolean;

    @attr({ mode: 'boolean' })
    supportsMove!: boolean;

    @attr({ mode: 'boolean' })
    supportsFlagging!: boolean;

    @attr({ mode: 'boolean' })
    supportsPinning!: boolean;

    @attr({ mode: 'boolean' })
    canPin!: boolean;

    @attr({ mode: 'boolean' })
    isFlagged!: boolean;

    @attr({ mode: 'boolean' })
    isComplete!: boolean;

    @attr({ mode: 'boolean' })
    isPinned!: boolean;

    @attr({ mode: 'boolean' })
    hideImportancePropertyIcon!: boolean;

    @attr({ mode: 'boolean' })
    hasAttachment!: boolean;

    @attr({ mode: 'boolean' })
    hasSharepointLink!: boolean;

    @attr({ mode: 'boolean' })
    effectiveMentioned!: boolean;

    @attr({ mode: 'boolean' })
    isSnoozed!: boolean;

    @attr({ mode: 'boolean' })
    isTaggedForBigScreen!: boolean;

    @attr({ mode: 'boolean' })
    isReminderSet!: boolean;

    @attr({ mode: 'boolean' })
    supportsHoverIcons!: boolean;

    @attr
    importance: ImportanceType = 'Normal';

    @attr({ converter: nullableNumberConverter })
    smimeType: SmimeType = 0;

    @attr({ converter: nullableNumberConverter })
    itemClassIcon: PropertyIcons = PropertyIcons.None;

    @attr({ converter: nullableNumberConverter })
    unreadCount!: number;

    @attr
    rowKey!: string;

    @attr
    tableViewId!: string;

    //#endregion

    //#region Element references
    @observable
    flagMenu!: FlagMenu;

    //#endregion

    //#region CSS classes
    @volatile
    get containerClassNames(): string {
        return classnames(
            'iconBarContainer',
            this.isSingleLine && this.isReadingPaneRight && 'iconBarNoLeftMargin'
        );
    }

    getHoverIconClassNames(key: HoverActionKey): string {
        return classnames(
            this.useDenselessIcons ? 'full' : this.densityModeString,
            this.shouldShowHoverActionsInReceivedColumn && this.hasPropertHoverIconIntersection
                ? this.doesHoverIconHavePropertyIconOverlap(key)
                    ? 'iconDisplay'
                    : this.doesPreviousHoverKeyHaveOverlap(key)
                    ? ['iconDisplay', 'iconHide']
                    : 'iconHide'
                : undefined
        );
    }

    getPropertyIconClassNames(icon: PropertyIcons): string {
        return classnames(
            this.useDenselessIcons ? 'denseless' : this.densityModeString,
            !this.shouldShowHoverActionsInReceivedColumn &&
                this.neverShowHoverIcons &&
                'alwaysShowPropertyIcons',
            this.shouldShowHoverActionsInReceivedColumn
                ? this.doesPreviousPropertyIconHaveOverlap(icon)
                    ? this.supportsHoverIcons
                        ? 'iconNoDisplay'
                        : 'alwaysShowPropertyIcons'
                    : 'alwaysShowPropertyIcons'
                : undefined
        );
    }
    //#endregion

    get hasPropertHoverIconIntersection(): boolean {
        return this.hoverActionKeys.some(key => this.doesHoverIconHavePropertyIconOverlap(key));
    }

    doesPreviousHoverKeyHaveOverlap(currentKey: HoverActionKey): boolean {
        const hoverKeys = this.hoverActionKeys;
        return hoverKeys
            .slice(0, hoverKeys.indexOf(currentKey))
            .some(key => this.doesHoverIconHavePropertyIconOverlap(key));
    }

    doesPreviousPropertyIconHaveOverlap(icon: PropertyIcons): boolean {
        const propertyIconKeys = this.propertyIconKeys;
        return propertyIconKeys
            .slice(0, propertyIconKeys.indexOf(icon))
            .some(key => this.doesPropertyIconHaveHoverIconOverlap(key));
    }

    doesPropertyIconHaveHoverIconOverlap(icon: PropertyIcons): boolean {
        if (this.isSingleLine) {
            return false;
        }
        switch (icon) {
            case PropertyIcons.Completed:
            case PropertyIcons.Flagged:
                return this.hoverActionKeys.includes('FlagUnflag');
            case PropertyIcons.Pinned:
                return this.hoverActionKeys.includes('PinUnpin');
            default:
                return false;
        }
    }

    doesHoverIconHavePropertyIconOverlap(key: HoverActionKey): boolean {
        switch (key) {
            case 'FlagUnflag': {
                const propertyIcons = this.propertyIconKeys;
                return (
                    propertyIcons.includes(PropertyIcons.Flagged) ||
                    propertyIcons.includes(PropertyIcons.Completed)
                );
            }
            case 'PinUnpin': {
                return this.propertyIconKeys.includes(PropertyIcons.Pinned);
            }
            default:
                return false;
        }
    }

    @mobxObservableProperty
    @volatile
    get shouldShowHoverActionsInReceivedColumn(): boolean {
        return this.isReadingPaneRight && this.isSingleLine;
    }

    @mobxObservableProperty
    @volatile
    get hoverActionKeys(): HoverActionKey[] {
        if (this.isInNotesFolder || this.isInOutboxFolder) {
            return this.isSingleLine ? ['Delete'] : [];
        }

        const hoverSurfaceActionKeys =
            getHoverSurfaceActionStore().hoverSurfaceActions ?? getHoverSurfaceAction();

        const unsupportedActionKeys: HoverActionKey[] = [];
        const supportedActionKeys: HoverActionKey[] = [];
        for (const actionKey of hoverSurfaceActionKeys) {
            const key = doesFolderOrViewSupportHoverAction(
                actionKey,
                this.canDelete,
                this.canArchive,
                this.supportsMove,
                this.supportsFlagging,
                this.supportsPinning,
                this.canPin
            )
                ? actionKey
                : 'None';

            if (key === 'None') {
                unsupportedActionKeys.push(key);
            } else {
                supportedActionKeys.push(key);
            }
        }
        // We want unsupported actions to be at the front of the list to get the correct alignment between prop icon and hover icon
        const filteredActionKeys = unsupportedActionKeys.concat(supportedActionKeys);

        if (!this.isSingleLine) {
            // Filter out big hover action
            const deleteIndex = filteredActionKeys.indexOf('Delete');
            const archiveIndex = filteredActionKeys.indexOf('Archive');
            if (deleteIndex !== -1) {
                filteredActionKeys.splice(deleteIndex, 1);
            } else if (archiveIndex !== -1) {
                filteredActionKeys.splice(archiveIndex, 1);
            }
        }

        if (this.shouldShowSettingsHoverButton) {
            filteredActionKeys[0] = 'Settings';
        }

        return filteredActionKeys;
    }

    @mobxObservableProperty
    @volatile
    get propertyIconKeys(): PropertyIcons[] {
        const icons: PropertyIcons[] = [];

        function addIcon(icon: PropertyIcons, index: number = MAX_PROPERTY_ICONS - 1) {
            while (index >= 0) {
                if (icons[index] === undefined) {
                    break;
                } else {
                    index -= 1;
                }
            }
            if (index >= 0) {
                icons[index] = icon;
            }
        }

        const hoverActionKeys: HoverActionKey[] = this.hoverActionKeys;

        const addFlagPropertyIcon = () => {
            if (
                this.supportsFlagging &&
                (this.isFlagged || this.isComplete) &&
                (!this.neverShowHoverIcons || !this.hoverActionKeys.includes('FlagUnflag'))
            ) {
                const icon = this.isComplete ? PropertyIcons.Completed : PropertyIcons.Flagged;
                const flagHoverIconIndex = hoverActionKeys.indexOf('FlagUnflag');
                const index =
                    flagHoverIconIndex == -1
                        ? undefined
                        : flagHoverIconIndex + (MAX_PROPERTY_ICONS - hoverActionKeys.length);
                addIcon(icon, index);
            }
        };

        const addPinnedPropertyIcon = () => {
            if (
                this.supportsPinning &&
                this.isPinned &&
                (!this.neverShowHoverIcons || !this.hoverActionKeys.includes('PinUnpin'))
            ) {
                const pinHoverIconIndex = this.hoverActionKeys.indexOf('PinUnpin');
                const index =
                    pinHoverIconIndex == -1
                        ? undefined
                        : pinHoverIconIndex + (MAX_PROPERTY_ICONS - hoverActionKeys.length);
                addIcon(PropertyIcons.Pinned, index);
            }
        };

        if (hoverActionKeys.includes('FlagUnflag')) {
            addFlagPropertyIcon();
            addPinnedPropertyIcon();
        } else {
            addPinnedPropertyIcon();
            addFlagPropertyIcon();
        }

        if (this.itemClassIcon && this.itemClassIcon !== PropertyIcons.None) {
            addIcon(this.itemClassIcon);
        }

        if (!this.hideImportancePropertyIcon) {
            if (this.importance == 'Low') {
                addIcon(PropertyIcons.ImportanceLow);
            } else if (this.importance == 'High') {
                addIcon(PropertyIcons.ImportanceHigh);
            }
        }

        switch (this.smimeType) {
            case -1:
            case 6:
            case 12:
            case 13:
            case 14:
                addIcon(PropertyIcons.LockClosedRegular);
                break;
            case 11:
            case 10:
                addIcon(PropertyIcons.RibbonRegular);
                break;
            case 0:
            default:
                break;
        }

        if (this.hasAttachment) {
            addIcon(PropertyIcons.Attachment);
        } else if (this.hasSharepointLink) {
            addIcon(PropertyIcons.Link);
        }

        if (this.effectiveMentioned) {
            addIcon(PropertyIcons.GlobalMentionedMe);
        }

        if (this.isSnoozed || this.isTaggedForBigScreen) {
            addIcon(PropertyIcons.Snooze);
        }

        if (this.isReminderSet && this.supportsFlagging && this.isFlagged && !this.isComplete) {
            addIcon(PropertyIcons.Reminder);
        }

        const firstIconIndex = icons.findIndex(icon => icon !== undefined);

        return firstIconIndex == -1 ? [] : icons.slice(firstIconIndex, MAX_PROPERTY_ICONS);
    }

    @mobxObservableProperty
    get isReadingPaneRight(): boolean {
        return isReadingPanePositionRight();
    }

    @mobxObservableProperty
    get densityModeString(): string {
        return getDensityModeString();
    }

    @volatile
    get useDenselessIcons(): boolean {
        return (
            isGlobalFeatureEnabled('mon-type-ramp') &&
            (!this.isSingleLine || this.densityModeString !== 'compact')
        );
    }

    @mobxObservableProperty
    get shouldShowSettingsHoverButton(): boolean {
        return !!getHoverSurfaceActionStore().showSettingsHoverAction;
    }

    private openFlagMenu = (evt: Event) => {
        const target = (evt as CustomEvent<DOMRect>).detail;
        this.flagMenu.menu.slottedMenuList[0].style.top = `${target.bottom}px`;
        this.flagMenu.menu.slottedMenuList[0].style.left = `${target.left}px`;
        this.flagMenu.menu.openMenu();
    };

    connectedCallback(): void {
        super.connectedCallback();
        this.shadowRoot?.addEventListener('openflagmenu', this.openFlagMenu);
    }

    disconnectedCallback(): void {
        super.disconnectedCallback();
        this.shadowRoot?.removeEventListener('openflagmenu', this.openFlagMenu);
    }
}

IconBar.define({
    name: 'owa-mail-list-item-icon-bar',
    template,
    styles,
    shadowOptions: {
        customElementRegistry: iconBarRegistry,
    },
});

declare global {
    namespace JSX {
        interface IntrinsicElements {
            'owa-mail-list-item-icon-bar': React.DetailedHTMLProps<
                React.HTMLProps<HTMLElement>,
                IconBar
            > & {
                isSingleLine: boolean;
                neverShowHoverIcons: boolean;
                neverShowPropertyIcons: boolean;
                isInNotesFolder: boolean;
                isInOutboxFolder: boolean;
                canDelete: boolean;
                canArchive: boolean;
                supportsMove: boolean;
                supportsFlagging: boolean;
                supportsPinning: boolean;
                canPin: boolean;
                isFlagged: boolean;
                isComplete: boolean;
                isPinned: boolean;
                hideImportancePropertyIcon: boolean;
                hasAttachment: boolean;
                hasSharepointLink: boolean;
                effectiveMentioned: boolean;
                isSnoozed: boolean;
                isTaggedForBigScreen: boolean;
                isReminderSet: boolean;
                importance: ImportanceType;
                smimeType: SmimeType;
                itemClassIcon: PropertyIcons;
                unreadCount: number;
                rowKey: string;
                tableViewId: string;
                supportsHoverIcons: boolean | undefined;
            };
        }
    }
}

/* eslint-enable owa-custom-rules/no-side-effects */
